import { Button, Divider, Grid, IconButton, List, ListItem, ListItemText, TextField, Tooltip, Typography, Switch, FormControlLabel, CircularProgress, Collapse } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ArrowCircleLeftTwoTone, ArrowCircleRightTwoTone, KeyboardDoubleArrowLeftOutlined, KeyboardDoubleArrowRightOutlined, ExpandMore } from '@mui/icons-material';
import Transition from '../common/Transition';
import { Header } from '../common/Header';
import DatasetIcon from '@mui/icons-material/Dataset';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SelectInput from '../use-form-components/SelectInput';
import { EndPoints } from '../../axios/endPoints';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';
import { 
    GetTrainingDatasetDetails, 
    handleMoveAllToColumnsFromInput, 
    handleMoveAllToColumnsFromOutput, 
    handleMoveAllToInputColumns, 
    handleMoveAllToOutputColumns, 
    handleMoveColumnToColumnsLeft, 
    handleMoveColumnToColumnsRight, 
    handleMoveColumnToInputLeft, 
    handleMoveColumnToOutputRight, 
    handleTrainNewModel,
    AIOrchestratorInitTrain,
    handleTrainNewModelAlgorithmList,
    GetModelAlgorithmDetails,
} from '../../store/mlcreator-slice/mlcreator';

interface Column {
    id: number;
    name: string;
    type: string;
}

interface AlgorithmModel {
    name: string;
    algorithmFunctionName: string,
    defaultExecutionTime: number;
    minExecutionTime: number;
    maxExecutionTime: number;
}

interface AlgorithmSetting {
    name: string;
    algorithmFunctionName: string,
    enabled: boolean;
    executionTime: number;
    minExecutionTime: number;
    maxExecutionTime: number;
}

const mainSchema = (t: Function) => yup.object().shape({
    modelName: yup.string().required(t('TRAIN_NEW_MODEL_NAME_REQUIRED')),
}).required();

const algorithmSchema = (t: Function) => yup.object().shape({
    modelType: yup.object().required(t('TRAIN_NEW_MODEL_TYPE_REQUIRED')),
    algorithms: yup.object()
}).required();

const TrainNewModelDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { 
        columns,
        outputColumns, 
        inputColumns, 
        trainNewModelDialogOpen, 
        selectedCreatorRow,
        fileDetailsLoading,
        aiInitiateTraining,
        trainNewModelAlgorithmDialogOpen,
    } = useAppSelector((state: RootState) => state.mlcreator);

    const [selectedModelName, setSelectedModelName] = useState('');
    const [filteredAlgorithms, setFilteredAlgorithms] = useState<AlgorithmSetting[]>([]);
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    
    const { control: mainControl, setValue: setMainValue, handleSubmit: handleMainSubmit, formState: { errors: mainErrors } } = useForm({  
        mode: "onBlur", 
        resolver: yupResolver(mainSchema(t)) 
    });

    const { control: algorithmControl, handleSubmit: handleAlgorithmSubmit, getValues: algorithmValues, setValue: setAlgorithmValue, formState: { errors: algorithmErrors } } = useForm({  
        mode: "onBlur", 
        resolver: yupResolver(algorithmSchema(t)) 
    });

    const modelTypeValue = useWatch({
        control: algorithmControl,
        name: 'modelType',
        defaultValue: {},
    });

    const handleModelTypeChange = () => {
        const selectedModelType = algorithmValues('modelType') as { id: number };
        const algorithmsForModelType = algorithmValues('algorithms') as { data: Record<string, AlgorithmModel[]> };

        const modelTypeId = selectedModelType?.id;
        const algorithmList = algorithmsForModelType?.data?.[modelTypeId?.toString()] || [];
        
        // Initialize algorithm settings with default values
        const updatedAlgorithms = algorithmList.map((algo: AlgorithmModel) => ({
            name: t(algo.name),
            algorithmFunctionName: algo.name,
            enabled: true,
            executionTime: algo.defaultExecutionTime,
            minExecutionTime: algo.minExecutionTime,
            maxExecutionTime: algo.maxExecutionTime
        }));
        
        setFilteredAlgorithms(updatedAlgorithms);
    };

    function handleMainClose() {
        dispatch(handleTrainNewModel({ isOpen: false }));
        dispatch(handleTrainNewModelAlgorithmList({ isOpen: false }));
        setMainValue('modelName', '');
        setSelectedModelName('');
        setIsConfirmLoading(false);
        setAdvancedSettingsOpen(false);
    }

    async function handleConfirm(data: any) {
        if (inputColumns.length === 0 || outputColumns.length === 0) {
            dispatch(enqueueSnackbar({ 
                message: t('INPUT_OUTPUT_COLUMNS_VALIDATION'),  
                options: { variant: 'error', persist: true }
            }));
            return;
        }
        setIsConfirmLoading(true);
        try {
            const response = await dispatch(GetModelAlgorithmDetails({
                mlTrainingDatasetId: selectedCreatorRow.id,
                outputColumns: outputColumns.map((el: Column) => el.name),
                inputColumns: inputColumns.map((el: Column) => el.name),
            }));
            const { payload } = response;
            if (payload) {
                dispatch(enqueueSnackbar({
                    message: t(payload.message),
                    options: { variant: handleNotificationVariant(payload.success), persist: !payload.success },
                }));
                setAlgorithmValue('modelType', {
                    id: payload.result.modelTypeId,
                    name: t(payload.result.modelTypeName),
                });
                setAlgorithmValue('algorithms', {
                    data: payload.result.algorithms 
                });
                setSelectedModelName(data.modelName);
                dispatch(handleTrainNewModelAlgorithmList({ isOpen: true }));
            }
        } catch (error) {
            dispatch(enqueueSnackbar({
                message: t('INITIATE_TRAINING_ERROR'), 
                options: { variant: 'error' },
            }));
            console.error('Training initiation error:', error);
        }
        finally {
            setIsConfirmLoading(false);
        }
    }

    async function handleTrain(data: any) {
        try {
            const response = await dispatch(AIOrchestratorInitTrain({
                mlTrainingDatasetId: selectedCreatorRow.id,
                mlModelName: selectedModelName,
                mlModelTypeId: data.modelType?.id ?? '',
                inputColumns: inputColumns.map((el: Column) => el.name),
                outputColumns: outputColumns.map((el: Column) => el.name),
                algorithms: filteredAlgorithms.filter(algo => algo.enabled).map((algo) => ({
                    algorithmName: algo.algorithmFunctionName,
                    executionTime: algo.executionTime,
                })),
            }));
            const { payload } = response;
            if (payload) {
                dispatch(enqueueSnackbar({
                    message: t(payload.message),
                    options: { variant: handleNotificationVariant(payload.success), persist: !payload.success },
                }));
                if (payload.success) handleMainClose();
            }
        } catch (error) {
            dispatch(enqueueSnackbar({
                message: t('INITIATE_TRAINING_ERROR'), 
                options: { variant: 'error' },
            }));
            console.error('Training initiation error:', error);
        }
    }

    const handleToggleAlgorithm = (index: number) => {
        setFilteredAlgorithms((prev) =>
            prev.map((algo, i) =>
                i === index ? { ...algo, enabled: !algo.enabled } : algo
            )
        );
    };

    const handleExecutionTimeChange = (index: number, value: string) => {
        setFilteredAlgorithms((prev) =>
            prev.map((algo, i) =>
                i === index ? { ...algo, executionTime: parseInt(value, 10) || 0 } : algo
            )
        );
    };

    const handleExecutionTimeBlur = (index: number) => {
        setFilteredAlgorithms((prev) => {
            const algo = prev[index];
            const clampedTime = Math.max(
                algo.minExecutionTime || 0,
                Math.min(algo.maxExecutionTime || Infinity, algo.executionTime)
            );
            return prev.map((a, i) =>
                i === index ? { ...a, executionTime: clampedTime } : a
            );
        });
    };

    const handleExecutionTimeKeyDown = (index: number, event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            setFilteredAlgorithms((prev) => {
                const algo = prev[index];
                const clampedTime = Math.max(
                    algo.minExecutionTime || 0,
                    Math.min(algo.maxExecutionTime || Infinity, algo.executionTime)
                );
                return prev.map((a, i) =>
                    i === index ? { ...a, executionTime: clampedTime } : a
                );
            });
        }
    };

    useEffect(() => {
        if (modelTypeValue && Object.keys(modelTypeValue).length > 0) {
            handleModelTypeChange();
            console.log("Model type changed:", modelTypeValue);
        }
    }, [modelTypeValue]);

    useEffect(() => {
        if (trainNewModelDialogOpen)
            dispatch(GetTrainingDatasetDetails(selectedCreatorRow.id));
        return () => {
            setMainValue('modelName', '');
            setAlgorithmValue('modelType', {});
        }
    }, [trainNewModelDialogOpen]);

    return (
        <React.Fragment>
            <Dialog
                open={trainNewModelDialogOpen}
                TransitionComponent={Transition}
                keepMounted
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick') handleMainClose();
                }}
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth="md"
                disableEnforceFocus
            >
                <form onSubmit={handleMainSubmit(handleConfirm)}>
                    <Header
                        title={t('TRAIN_NEW_MODEL_DIALOG_TITLE')}
                        helperIcon={
                            <RedirectIconButton to={'/help/3'} className="helper-icon">
                                <ContactSupportIcon />
                            </RedirectIconButton>
                        }
                    />
                    <DialogContent>
                        <Grid container display="flex" flexDirection="column" spacing={2}>
                            <Grid
                                item
                                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                                <DatasetIcon style={{ fontSize: '20px', color: 'darkgray' }} />
                                <Typography ml={1} variant="body1">
                                    {t('TRAIN_NEW_MODEL_DATASET_LABEL', { name: selectedCreatorRow.name })}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            <Grid item>
                                <Controller
                                    control={mainControl}
                                    name="modelName"
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            variant="outlined"
                                            label={t('TRAIN_NEW_MODEL_NAME_FIELD')}
                                            type="text"
                                            error={error !== undefined}
                                            helperText={error ? mainErrors.modelName?.message : ''}
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                            {fileDetailsLoading ? (
                                <Grid item p={2}>
                                    <Box sx={{ width: '100%' }}>
                                        <LinearProgress />
                                    </Box>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <Grid
                                        container
                                        display="flex"
                                        spacing={2}
                                        direction={{ xs: 'column', md: 'row' }}
                                        sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
                                    >
                                        {/* Input Columns */}
                                        <Grid item xs={12} md={3}>
                                            <Grid container display="flex" flexDirection="column" spacing={1} sx={{ minWidth: 0 }}>
                                                <Grid item>
                                                    <Typography variant="button">
                                                        {t('TRAIN_NEW_MODEL_INPUT_COLUMNS_LABEL')}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    item
                                                    id="train-new-model-dialog-border"
                                                    display="flex"
                                                    flexDirection="column"
                                                    sx={{ borderRadius: '5px', height: '200px', overflowY: 'auto' }}
                                                >
                                                    <Grid container>
                                                        {inputColumns?.map((el: Column, index: number) => (
                                                            <Grid
                                                                key={index}
                                                                xs={12}
                                                                item
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    position: 'relative',
                                                                    backgroundColor: 'transparent',
                                                                    color: 'inherit',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f0f0f0',
                                                                    '& .arrow-box-left': { opacity: 1 },
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    ml={1}
                                                                    py={0.5}
                                                                    sx={{ pr: 5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                                >
                                                                    { `• ${el.name} / ${t(el.type)}` }
                                                                </Typography>
                                                                <Box
                                                                    className="arrow-box-left"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        right: 8,
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        opacity: 0,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        transition: 'opacity 0.2s',
                                                                        '&:hover': { opacity: 1 },
                                                                        pointerEvents: 'auto',
                                                                        backgroundColor: "#f0f0f0"
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        dispatch(handleMoveColumnToColumnsRight(el));
                                                                    }}
                                                                >
                                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_TO_RIGHT')}>
                                                                        <ArrowCircleRightTwoTone sx={{ fontSize: '24px', color: 'red' }} />
                                                                    </Tooltip>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/* Move All Buttons */}
                                        <Grid
                                            item
                                            xs={12}
                                            md={1}
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            sx={{
                                                padding: '0 !important',
                                                mt: { xs: 0, md: 3 },
                                            }}
                                        >
                                            <Grid
                                                container
                                                display="flex"
                                                flexDirection={{ xs: 'row', md: 'column' }}
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{ width: 'auto' }}
                                            >
                                                <Grid item>
                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_ALL_TO_LEFT')}>
                                                        <IconButton onClick={() => dispatch(handleMoveAllToInputColumns())}>
                                                            <KeyboardDoubleArrowLeftOutlined sx={{ fontSize: '30px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item>
                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_ALL_TO_RIGHT')}>
                                                        <IconButton onClick={() => dispatch(handleMoveAllToColumnsFromInput())}>
                                                            <KeyboardDoubleArrowRightOutlined sx={{ fontSize: '30px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/* Columns */}
                                        <Grid item xs={12} md={4}>
                                            <Grid container display="flex" flexDirection="column" spacing={1} sx={{ minWidth: 0 }}>
                                                <Grid item>
                                                    <Typography variant="button">
                                                        {t('TRAIN_NEW_MODEL_COLUMNS_LABEL')}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    item
                                                    id="train-new-model-dialog-border"
                                                    display="flex"
                                                    flexDirection="column"
                                                    sx={{ borderRadius: '5px', height: '200px', overflowY: 'auto' }}
                                                >
                                                    <Grid container>
                                                        {columns?.map((el: Column, index: number) => (
                                                            <Grid
                                                                key={index}
                                                                xs={12}
                                                                item
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    position: 'relative',
                                                                    backgroundColor: 'transparent',
                                                                    color: 'inherit',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f0f0f0',
                                                                    '& .arrow-box-center': { opacity: 1 },
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    ml={1}
                                                                    py={0.5}
                                                                    sx={{ pr: 7, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                                >
                                                                    { `• ${el.name} / ${t(el.type)}` }
                                                                </Typography>
                                                                <Box
                                                                    className="arrow-box-center"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        right: 8,
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        opacity: 0,
                                                                        transition: 'opacity 0.2s',
                                                                        display: 'flex',
                                                                        gap: 1,
                                                                        '&:hover': { opacity: 1 },
                                                                        pointerEvents: 'auto',
                                                                        backgroundColor: "#f0f0f0"
                                                                    }}
                                                                >
                                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_TO_LEFT')}>
                                                                        <ArrowCircleLeftTwoTone
                                                                            sx={{ fontSize: '24px', color: 'green' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                dispatch(handleMoveColumnToInputLeft(el));
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_TO_RIGHT')}>
                                                                        <ArrowCircleRightTwoTone
                                                                            sx={{ fontSize: '24px', color: 'green' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                dispatch(handleMoveColumnToOutputRight(el));
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/* Move All Buttons */}
                                        <Grid
                                            item
                                            xs={12}
                                            md={1}
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            sx={{
                                                padding: '0 !important',
                                                mt: { xs: 0, md: 3 },
                                            }}
                                        >
                                            <Grid
                                                container
                                                display="flex"
                                                flexDirection={{ xs: 'row', md: 'column' }}
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{ width: 'auto' }}
                                            >
                                                <Grid item>
                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_ALL_TO_LEFT')}>
                                                        <IconButton onClick={() => dispatch(handleMoveAllToColumnsFromOutput())}>
                                                            <KeyboardDoubleArrowLeftOutlined sx={{ fontSize: '30px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item>
                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_ALL_TO_RIGHT')}>
                                                        <IconButton onClick={() => dispatch(handleMoveAllToOutputColumns())}>
                                                            <KeyboardDoubleArrowRightOutlined sx={{ fontSize: '30px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/* Output Columns */}
                                        <Grid item xs={12} md={3}>
                                            <Grid container display="flex" flexDirection="column" spacing={1} sx={{ minWidth: 0 }}>
                                                <Grid item>
                                                    <Typography variant="button">
                                                        {t('TRAIN_NEW_MODEL_OUTPUT_COLUMNS_LABEL')}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    item
                                                    id="train-new-model-dialog-border"
                                                    display="flex"
                                                    flexDirection="column"
                                                    sx={{ borderRadius: '5px', height: '200px', overflowY: 'auto' }}
                                                >
                                                    <Grid container>
                                                        {outputColumns?.map((el: Column, index: number) => (
                                                            <Grid
                                                                key={index}
                                                                xs={12}
                                                                item
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    position: 'relative',
                                                                    backgroundColor: 'transparent',
                                                                    color: 'inherit',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f0f0f0',
                                                                    '& .arrow-box-right': { opacity: 1 },
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    className="arrow-box-right"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        left: 8,
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        opacity: 0,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        transition: 'opacity 0.2s',
                                                                        '&:hover': { opacity: 1 },
                                                                        pointerEvents: 'auto',
                                                                        backgroundColor: "#f0f0f0"
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        dispatch(handleMoveColumnToColumnsLeft(el));
                                                                    }}
                                                                >
                                                                    <Tooltip title={t('TRAIN_NEW_MODEL_MOVE_TO_LEFT')}>
                                                                        <ArrowCircleLeftTwoTone sx={{ fontSize: '24px', color: 'red' }} />
                                                                    </Tooltip>
                                                                </Box>
                                                                <Grid item sx={{ textAlign: 'right', width: '100%' }}>
                                                                    <Typography
                                                                        ml={1}
                                                                        py={0.5}
                                                                        sx={{ pl: 5, pr: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                                    >
                                                                        { `• ${el.name} / ${t(el.type)}` }
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    {isConfirmLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress size={60} sx={{ color: '#1976d2' }} />
                        </Box>
                    )}
                    <Divider />
                    <DialogActions>
                        <Button
                            sx={{ color: '#436372', marginRight: '10px' }}
                            onClick={handleMainClose}
                        >
                            {t('CANCEL_BUTTON')}
                        </Button>
                        <Button
                            variant="contained"
                            className="button-color"
                            type="submit"
                            disabled={isConfirmLoading}
                        >
                            {t('PROCEED_BUTTON')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            <Dialog
                open={trainNewModelAlgorithmDialogOpen}
                TransitionComponent={Transition}
                keepMounted
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick' && !aiInitiateTraining) {
                        dispatch(handleTrainNewModelAlgorithmList({ isOpen: false }));
                        setAlgorithmValue('modelType', {});
                        setAdvancedSettingsOpen(false);
                    }
                }}
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth="md"
            >
                <form onSubmit={handleAlgorithmSubmit(handleTrain)}>
                    <Header title={t('TRAIN_NEW_MODEL_ALGORITHM_DETAILS_TITLE')} />                    
                    <DialogContent sx={aiInitiateTraining ? { pointerEvents: 'none' } : {}}>
                        <Grid container display="flex" flexDirection="column" spacing={2}>
                        <Grid item container direction="column" spacing={1}>
                                <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DatasetIcon sx={{ fontSize: '20px', color: 'darkgray' }} />
                                    <Typography variant="body1">
                                        {t('TRAIN_NEW_MODEL_DATASET_LABEL', { name: selectedCreatorRow.name })}
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                                    <ModelTrainingIcon sx={{ fontSize: '20px', color: 'darkgray' }} />
                                    <Typography variant="body1">
                                        {t('TRAIN_NEW_MODEL_NAME_FIELD_LABEL', { name: selectedModelName })}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item><Divider /></Grid>
                            <Grid item>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: '12px',
                                        backgroundColor: '#fafafa',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0',
                                        transition: 'background-color 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                    onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
                                >
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ fontWeight: 500, color: '#424242' }}
                                    >
                                        {t('ADVANCED_SETTINGS_TITLE')}
                                    </Typography>
                                    <ExpandMore
                                        sx={{
                                            transform: advancedSettingsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s',
                                            marginLeft: 'auto',
                                            color: '#757575',
                                        }}
                                    />
                                </Box>
                                <Collapse in={advancedSettingsOpen}>
                                    <Box
                                        sx={{
                                            mt: '10px',
                                            borderRadius: '8px',
                                            p: '16px',
                                            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                                            border: '1px solid #e0e0e0',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        <SelectInput 
                                            url={EndPoints.ML_CREATOR_GET_AI_MODEL_TYPES + "?recommendedModelType=" + (algorithmValues('modelType') as { id: number })?.id}
                                            name="modelType"
                                            label={t('TRAIN_NEW_MODEL_TYPE_FIELD')}
                                            idName="id"                                    
                                            labelName="name"
                                            multiple={false}
                                            disabled={false}
                                            control={algorithmControl}
                                            translateName
                                            sx={{ mb: '16px' }}
                                        />

                                        <Box sx={{ mb: 2 }}>
                                            <Grid container sx={{ borderBottom: '2px solid #e0e0e0', pb: 1, p: '5px' }}>
                                                <Grid item xs={4}>
                                                    <Typography variant="subtitle2" fontWeight={500} color="#424242">
                                                        {t('ALGORITHM_LIST_COLUMNS_ALGORITHM_NAME')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="subtitle2" fontWeight={500} color="#424242">
                                                        {t('ALGORITHM_LIST_COLUMNS_STATUS')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography variant="subtitle2" marginLeft={'10px'} fontWeight={500} color="#424242">
                                                        {t('ALGORITHM_LIST_COLUMNS_EXECUTION_TIME')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={1}>
                                                    <Typography variant="subtitle2" marginLeft={'10px'} textAlign={'center'} fontWeight={500} color="#424242">
                                                        {t('ALGORITHM_LIST_COLUMNS_INFO')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <List sx={{ p: 0, maxHeight: '300px', overflowY: 'auto' }}>
                                            {filteredAlgorithms.map((algorithm, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: '5px',
                                                    mb: '8px',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e8ecef',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                        transform: 'translateY(-2px)',
                                                        backgroundColor: '#f9fbfc',
                                                    },
                                                }}
                                            >
                                                <Grid container alignItems="center" spacing={2}>
                                                    <Grid item xs={4}>
                                                        <ListItemText 
                                                            primary={algorithm.name}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.95rem',
                                                                fontWeight: 500,
                                                                color: '#1a3c5e',
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={3}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={algorithm.enabled}
                                                                    onChange={() => handleToggleAlgorithm(index)}
                                                                    color="primary"
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiSwitch-track': {
                                                                        backgroundColor: algorithm.enabled ? '#388e3c' : '#d32f2f',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={algorithm.enabled ? t('ENABLED') : t('DISABLED')}
                                                            sx={{
                                                                '& .MuiFormControlLabel-label': {
                                                                color: algorithm.enabled ? '#388e3c' : '#d32f2f',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 400,
                                                                },
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            type="number"
                                                            value={algorithm.executionTime}
                                                            onChange={(e) => handleExecutionTimeChange(index, e.target.value)}
                                                            onBlur={() => handleExecutionTimeBlur(index)}
                                                            onKeyDown={(e) => handleExecutionTimeKeyDown(index, e)}
                                                            //label={t('EXECUTION_TIME_SECONDS')}
                                                            size="small"                                                            
                                                            inputProps={{
                                                                min: algorithm.minExecutionTime,
                                                                max: algorithm.maxExecutionTime,
                                                            }}
                                                            sx={{
                                                                width: "200px",
                                                                '& .MuiInputBase-root': {
                                                                    borderRadius: '6px',
                                                                    backgroundColor: '#f9f9f9',
                                                                    transition: 'background-color 0.2s',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f0f4f8',
                                                                    },
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    fontSize: '0.85rem',
                                                                    color: '#424242',
                                                                },
                                                                '& .Mui-focused .MuiInputLabel-root': {
                                                                    color: '#1976d2',
                                                                },
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                                        <Tooltip
                                                            title={t('TRAIN_NEW_MODEL_MIN_MAX_INFO', {
                                                                min: algorithm.minExecutionTime,
                                                                max: algorithm.maxExecutionTime,
                                                            })}
                                                        >
                                                            <IconButton
                                                                color="info"
                                                                sx={{
                                                                    '&:hover': { backgroundColor: '#e3f2fd' },
                                                                    p: '6px',
                                                                }}
                                                            >
                                                                <InfoIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </Collapse>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <Divider />
                    {aiInitiateTraining ? 
                        <Box sx={{ width: '100%', padding: '16px' }}><LinearProgress /></Box>
                        :
                        <DialogActions>
                            <Button 
                                sx={{ color: '#436372', marginRight: '10px' }} 
                                onClick={() => {
                                    dispatch(handleTrainNewModelAlgorithmList({ isOpen: false }));
                                    setAlgorithmValue('modelType', {});
                                }}
                            >
                                {t('CANCEL_BUTTON')}
                            </Button>
                            <Button variant='contained' className="button-color" type="submit">{t('TRAIN_BUTTON')}</Button>
                        </DialogActions>
                    }
                </form>
            </Dialog>
        </React.Fragment>
    );
}

export default TrainNewModelDialog;
